import { useState, useRef, useEffect } from "react";
import { Flag, Check, Ban } from "lucide-react";

const colors = {
    urgent: "text-red-600",
    high: "text-orange-500",
    normal: "text-blue-600",
    low: "text-green-600",
};

const bgColors = {
    urgent: "bg-red-100",
    high: "bg-orange-100",
    normal: "bg-blue-100",
    low: "bg-green-100",
};

const labels = {
    urgent: "Urgent",
    high: "High",
    normal: "Normal",
    low: "Low",
};

const levels = [
    { key: "urgent", label: "Urgent" },
    { key: "high", label: "High" },
    { key: "normal", label: "Normal" },
    { key: "low", label: "Low" },
];

export default function TaskPriority({
    value,
    onChange,
    showclear = true,
    placement = "bottom",
    align = "left",
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef();

    useEffect(() => {
        if (!showclear) return;
        function handle(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("mousedown", handle);
        return () => document.removeEventListener("mousedown", handle);
    }, [showclear]);

    const positionClass = placement === "top" ? "bottom-full mb-2" : "top-full mt-2";
    const alignClass =
        align === "right"
            ? "right-0"
            : align === "center"
                ? "left-1/2 -translate-x-1/2"
                : "left-0";

    return (
        <div className="relative inline-flex" ref={ref}>
            <div
                onClick={() => showclear && setOpen((p) => !p)}
                className={`cursor-pointer inline-flex items-center gap-1 md:gap-2 rounded sm:rounded-lg px-2 md:px-3 py-1 text-[11px] md:text-sm font-medium
          ${value ? bgColors[value] : "bg-white border border-gray-300"} transition-colors hover:shadow-sm`}
            >
                <Flag size={16} className={value ? colors[value] : "text-gray-400"} />
                {value ? (
                    <span className="capitalize">{labels[value]}</span>
                ) : (
                    <span className="hidden xl:inline text-gray-400 text-sm">
                        Select priority
                    </span>
                )}
            </div>

            {showclear && open && (
                <div
                    className={`absolute ${positionClass} ${alignClass} mt-1 w-36 md:w-44 max-h-60 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-y-auto`}
                >
                    {levels.map((l) => (
                        <div
                            key={l.key}
                            onClick={() => {
                                onChange(l.key);
                                setOpen(false);
                            }}
                            className={`flex items-center justify-between px-2 md:px-3 py-1 md:py-2 cursor-pointer rounded hover:bg-gray-100 transition-colors
                ${value === l.key ? "bg-gray-100 font-semibold" : "text-gray-800"} text-[10px] md:text-sm`}
                        >
                            <div className="flex items-center gap-1 md:gap-2">
                                <Flag size={16} className={colors[l.key]} />
                                <span>{l.label}</span>
                            </div>
                            {value === l.key && <Check size={16} className="text-purple-600" />}
                        </div>
                    ))}

                    <div className="border-t border-gray-200 my-1"></div>

                    <div
                        onClick={() => {
                            onChange("");
                            setOpen(false);
                        }}
                        className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1 md:py-2 cursor-pointer hover:bg-red-100 text-red-600 rounded transition-colors text-[10px] md:text-sm"
                    >
                        <Ban size={16} />
                        <span>Clear</span>
                    </div>
                </div>
            )}
        </div>
    );
}